import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';
import logger from '@/logger';
import { Chat, ChatMessage, MessageType } from './types';
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

const PAGE_SIZE = 10;

export interface SendMessageOptions {
	type?: 'text' | 'voice' | 'audio';
	audioUrl?: string;
}

export interface SendImageOptions {
	imageUrl?: string;
}

export interface SendDocumentOptions {
	documentUrl?: string;
}

interface ChatStore {
	// State
	chats: Chat[];
	selectedChat: Chat | null;
	messages: ChatMessage[];
	isLoading: boolean;
	isLoadingMessages: boolean;
	error: string | null;
	hasMore: boolean;
	tagLookup: Record<string, string>;

	// Actions
	loadFilteredChats: (
		isLoadMore?: boolean,
		tag?: string,
		statuses?: string[],
		search?: string
	) => Promise<void>;
	loadMoreChats: () => Promise<void>;
	selectChat: (chat: Chat | null) => void;
	loadMessages: (chatId: string) => Promise<void>;
	sendMessage: (chatId: string, content: string, options?: SendMessageOptions) => Promise<boolean>;
	sendImageMessage: (chatId: string, options?: SendImageOptions) => Promise<boolean>;
	sendDocumentMessage: (chatId: string, options?: SendDocumentOptions) => Promise<boolean>;
	resetError: () => void;
	selectedTag: string;
	selectedStatuses: string[];
	searchTerm: string;
	setFilters: (filters: { tag?: string; statuses?: string[]; search?: string }) => void;
	getTagLookup: () => Promise<void>;
}

export const useChatStore = create<ChatStore>((set, get) => ({
	chats: [],
	selectedChat: null,
	messages: [],
	isLoading: true,
	isLoadingMessages: false,
	error: null,
	hasMore: true,
	selectedTag: '',
	selectedStatuses: [
		'CLIENT_HAS_NOT_ANSWERED',
		'NEEDS_REVIEW',
		'AI_RESPONSES',
		'ANSWER_SENT',
		'ARCHIVED',
		'PREAPPROVED'
	],
	searchTerm: '',
	tagLookup: {},

	loadFilteredChats: async (isLoadMore = false) => {
		try {
			set(() => ({ isLoading: !isLoadMore }));

			const { selectedTag, selectedStatuses, searchTerm } = get();
			const currentChats = get().chats;
			const from = isLoadMore ? currentChats.length : 0;
			const to = from + PAGE_SIZE - 1;

			let query = supabase
				.from('chats')
				.select('*', { count: 'exact' })
				.order('last_message_at', { ascending: false });

			if (selectedTag) {
				query = query.eq('tag', selectedTag);
			}

			if (selectedStatuses && selectedStatuses.length > 0) {
				query = query.in('customer_support_status', selectedStatuses);
			}

			const normalizedSearchTermForPhone = searchTerm.trim().replace(/^\+/, '').slice(-10);

			if (searchTerm && searchTerm.trim()) {
				query = query.or(
					`customer_phone.ilike.%${normalizedSearchTermForPhone}%,customer_name.ilike.%${searchTerm.trim()}%`
				);
			}

			const { data, error, count } = await query.range(from, to);

			if (error) {
				console.error('Supabase error:', error);
				set({ error: error.message });
				return;
			}

			const updatedChats = isLoadMore ? [...currentChats, ...(data || [])] : data || [];

			const loadedSoFar = from + (data?.length || 0);
			const hasMoreItems = count ? loadedSoFar < count : false;

			// Auto-select chat if there's only one result from a search
			const shouldAutoSelect =
				!isLoadMore && searchTerm && searchTerm.trim() && updatedChats.length === 1;

			set({
				chats: updatedChats,
				hasMore: hasMoreItems,
				isLoading: false,
				selectedChat: shouldAutoSelect ? updatedChats[0] : null
			});

			if (shouldAutoSelect && updatedChats[0]?.id) {
				get().loadMessages(updatedChats[0].id);
			}
		} catch (err) {
			console.error('Load chats error:', err);
			set({
				error: 'Failed to load chats',
				isLoading: false
			});
		}
	},
	setFilters: ({ tag, statuses, search }) => {
		set((state) => ({
			selectedTag: tag !== undefined ? tag : state.selectedTag,
			selectedStatuses: statuses !== undefined ? statuses : state.selectedStatuses,
			searchTerm: search !== undefined ? search : state.searchTerm,
			chats: [],
			hasMore: true
		}));

		get().loadFilteredChats(false);
	},
	loadMoreChats: async () => {
		const { hasMore, isLoading } = get();
		if (!hasMore || isLoading) {
			console.log('Skipping load more:', { hasMore, isLoading });
			return;
		}

		await get().loadFilteredChats(true);
	},

	selectChat: (chat) => {
		console.log('Selecting chat:', chat);

		const newSelectedChat = chat ? { ...chat } : null;

		set(() => ({
			selectedChat: newSelectedChat,
			messages: []
		}));

		if (newSelectedChat?.id) {
			get().loadMessages(newSelectedChat.id);
		}
	},

	loadMessages: async (chatId) => {
		if (!chatId) return;

		try {
			set({ messages: [], isLoadingMessages: true });
			const { data, error } = await supabase
				.from('chat_messages')
				.select('*')
				.eq('chat_id', chatId)
				.order('sent_at', { ascending: true })
				.order('created_at', { ascending: true });

			if (error) throw error;
			set({ messages: data || [] });
		} catch (err) {
			logger.error('Error loading messages:', err);
			set({ error: 'Failed to load messages' });
		} finally {
			set({ isLoadingMessages: false });
		}
	},

	sendMessage: async (chatId: string, content: string, options?: SendMessageOptions) => {
		try {
			const {
				data: { session },
				error: sessionError
			} = await supabase.auth.getSession();

			if (sessionError) throw sessionError;
			if (!session) throw new Error('No active session');

			// Find the current chat
			const currentChat = get().chats.find((c) => c.id === chatId);
			if (!currentChat?.is_from_whatsapp || !currentChat?.customer_phone) {
				logger.error('Invalid chat or missing phone number');
				return false;
			}

			const sentByUserId = session.user.id;

			const messageType = options?.type || 'text';
			const endpoint = 'whatsapp-send-message';
			const payloadType = messageType === 'voice' || messageType === 'audio' ? 'audio' : 'text';

			const payload: {
				chatId: string;
				phone: string;
				type: 'text' | 'audio';
				message?: string;
				audioUrl?: string;
				sentBy: string;
			} = {
				chatId,
				phone: currentChat.customer_phone,
				type: payloadType,
				sentBy: sentByUserId
			};

			// Add type-specific payload data
			if (messageType === 'text') {
				if (!content.trim()) {
					logger.error('Empty text message');
					return false;
				}
				payload.message = content.trim();
			} else if (messageType === 'voice' || messageType === 'audio') {
				if (!options?.audioUrl) {
					logger.error('Missing audio URL');
					return false;
				}
				payload.audioUrl = options.audioUrl;
			} else {
				logger.error(`Unsupported message type: ${messageType}`);
				return false;
			}

			// Log the request for debugging
			logger.debug('Sending message:', {
				endpoint,
				messageType,
				chatId,
				sentBy: sentByUserId,
				hasAudioUrl: !!options?.audioUrl
			});

			// Send message via WhatsApp
			const response = await fetch(`${supabaseUrl}/functions/v1/${endpoint}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${session.access_token}`
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				const errorText = await response.text();
				logger.error(`WhatsApp ${messageType} send error:`, errorText);
				set({ error: `Failed to send WhatsApp ${messageType} message` });
				return false;
			}

			// Log success
			logger.debug(`${messageType} message sent successfully`);
			logger.debug('selected chat', get().selectedChat);
			return true;
		} catch (err) {
			logger.error('Error sending message:', err);
			set({ error: 'Failed to send message' });
			return false;
		}
	},

	sendImageMessage: async (chatId: string, options?: SendImageOptions) => {
		try {
			const {
				data: { session },
				error: sessionError
			} = await supabase.auth.getSession();

			if (sessionError) throw sessionError;
			if (!session) throw new Error('No active session');

			// Find the current chat
			const currentChat = get().chats.find((c) => c.id === chatId);
			if (!currentChat?.is_from_whatsapp || !currentChat?.customer_phone) {
				logger.error('Invalid chat or missing phone number');
				return false;
			}

			const messageType = MessageType.IMAGE;
			const endpoint = 'whatsapp-send-image';

			// Construct base payload
			const payload: {
				chatId: string;
				phone: string;
				type: MessageType.IMAGE;
				imageUrl?: string;
			} = {
				chatId, // Always include chatId for proper file organization
				phone: currentChat.customer_phone,
				type: messageType
			};

			// Add type-specific payload data
			if (messageType === 'image') {
				if (!options?.imageUrl) {
					logger.error('Missing image URL');
					return false;
				}
				payload.imageUrl = options.imageUrl;
			} else {
				logger.error(`Unsupported message type: ${messageType}`);
				return false;
			}

			// Log the request for debugging
			logger.debug('Sending message:', {
				endpoint,
				messageType,
				chatId,
				imageURL: !!options?.imageUrl
			});

			// Send message via WhatsApp
			const response = await fetch(`${supabaseUrl}/functions/v1/${endpoint}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${session.access_token}`
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				const errorText = await response.text();
				logger.error(`WhatsApp ${messageType} send error:`, errorText);
				set({ error: `Failed to send WhatsApp ${messageType} message` });
				return false;
			}

			// Log success
			logger.debug(`${messageType} image sent successfully`);
			return true;
		} catch (err) {
			logger.error('Error sending message:', err);
			set({ error: 'Failed to send message' });
			return false;
		}
	},

	sendDocumentMessage: async (chatId: string, options?: SendDocumentOptions) => {
		try {
			const {
				data: { session },
				error: sessionError
			} = await supabase.auth.getSession();

			if (sessionError) throw sessionError;
			if (!session) throw new Error('No active session');

			// Find the current chat
			const currentChat = get().chats.find((c) => c.id === chatId);
			if (!currentChat?.is_from_whatsapp || !currentChat?.customer_phone) {
				logger.error('Invalid chat or missing phone number');
				return false;
			}

			const messageType = MessageType.DOCUMENT;
			const endpoint = 'whatsapp-send-document';

			// Construct base payload
			const payload: {
				chatId: string;
				phone: string;
				type: MessageType.DOCUMENT;
				documentUrl?: string;
			} = {
				chatId, // Always include chatId for proper file organization
				phone: currentChat.customer_phone,
				type: messageType
			};

			// Add type-specific payload data
			if (messageType === MessageType.DOCUMENT) {
				if (!options?.documentUrl) {
					logger.error('Missing document URL');
					return false;
				}
				payload.documentUrl = options.documentUrl;
			} else {
				logger.error(`Unsupported message type: ${messageType}`);
				return false;
			}

			// Log the request for debugging
			logger.debug('Sending message:', {
				endpoint,
				messageType,
				chatId,
				documentUrl: !!options?.documentUrl
			});

			// Send message via WhatsApp
			const response = await fetch(`${supabaseUrl}/functions/v1/${endpoint}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${session.access_token}`
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				const errorText = await response.text();
				logger.error(`WhatsApp ${messageType} send error:`, errorText);
				set({ error: `Failed to send WhatsApp ${messageType} message` });
				return false;
			}

			// Log success
			logger.debug(`${messageType} message sent successfully`);
			return true;
		} catch (err) {
			logger.error('Error sending document message:', err);
			set({ error: 'Failed to send document message' });
			return false;
		}
	},

	resetError: () => set({ error: null }),

	getTagLookup: async () => {
		try {
			const { data, error } = await supabase
				.from('templates')
				.select('template_name, recommended_tag');

			if (error) {
				console.error('Error fetching tags:', error);
				return;
			}

			const tagLookup: Record<string, string> = {};
			data?.forEach((template) => {
				tagLookup[template.template_name] = template.recommended_tag;
			});
			set({ tagLookup });
		} catch (err) {
			console.error('Error fetching tags:', err);
		}
	}
}));

// Subscribe to real-time updates
export const subscribeToChats = () => {
	const chatChannel = supabase
		.channel('chats')
		.on('postgres_changes', { event: '*', schema: 'public', table: 'chats' }, (payload) => {
			console.log('Real-time chat payload:', payload);

			switch (payload.eventType) {
				case 'INSERT':
					useChatStore.setState((state) => ({
						chats: [payload.new as Chat, ...state.chats]
					}));
					break;
				case 'UPDATE':
					useChatStore.setState((state) => ({
						chats: state.chats.map((chat) =>
							chat.id === (payload.new as Chat).id ? { ...chat, ...(payload.new as Chat) } : chat
						),
						selectedChat:
							state.selectedChat?.id === (payload.new as Chat).id
								? { ...state.selectedChat, ...(payload.new as Chat) }
								: state.selectedChat
					}));
					break;
				case 'DELETE':
					useChatStore.setState((state) => ({
						chats: state.chats.filter((chat) => chat.id !== (payload.old as Chat).id)
					}));
					break;
			}
		})
		.subscribe((_status, err) => {
			if (err) console.error('Chats subscription error:', err);
		});

	return chatChannel;
};

// Subscribe to real-time message updates
export const subscribeToMessages = (chatId: string) => {
	if (!chatId) return null;

	console.log(`🔔 Setting up real-time message subscription for chat ${chatId}`);

	const messageChannel = supabase
		.channel(`chat_${chatId}`)
		.on(
			'postgres_changes',
			{
				event: '*',
				schema: 'public',
				table: 'chat_messages',
				filter: `chat_id=eq.${chatId}`
			},
			(payload) => {
				console.log('🚨 REAL-TIME MESSAGE PAYLOAD:', payload);

				// Update messages based on the event type
				useChatStore.setState((state: any) => {
					switch (payload.eventType) {
						case 'INSERT':
							return {
								messages: [...state.messages, payload.new]
							};
						case 'UPDATE':
							return {
								messages: state.messages.map((msg: any) =>
									msg.id === payload.new.id ? { ...msg, ...payload.new } : msg
								)
							};
						case 'DELETE':
							return {
								messages: state.messages.filter((msg: any) => msg.id !== payload.old.id)
							};
						default:
							return state;
					}
				});
			}
		)
		.subscribe((status, err) => {
			console.log('📡 Messages subscription status:', status);
			if (err) console.error('❌ Messages subscription error:', err);
		});

	return messageChannel;
};

// Initialize chat store
export const initializeChatStore = async () => {
	const store = useChatStore.getState();
	await store.loadFilteredChats();
	await store.getTagLookup();
};
